import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { SearchExecutionActivity } from "@/lib/search-activity";
import { getDisplayPeriodQuery } from "@/lib/display-period";
import { useTRPC } from "@/lib/trpc";
import type { BrandDashboardPeriod } from "@/state/store/userPreferences";
import { MentionRateTooltip } from "../tooltips/mention-rate-tooltip";
import { RankingTooltip } from "../tooltips/ranking-tooltip";
import { SentimentScoreTooltip } from "../tooltips/sentiment-score-tooltip";
import { BaseChart, type BaseChartConfig, type ChartType } from "./base-chart";
import type { ChartBrand, ChartDataPoint } from "./types";

interface ChartProps {
      brandId: string;
      period: BrandDashboardPeriod;
      activity?: SearchExecutionActivity | null;
      chartType?: ChartType;
      compact?: boolean;
      highlightedCompetitorId?: string | null;
      showCompetitors?: boolean;
      hiddenCompetitorIds?: ReadonlySet<string>;
}

// ============================================================================
// VISIBILITY SCORE CHART - Shows your brand vs competitors
// ============================================================================

const visibilityConfig: BaseChartConfig = {
      title: "Visibility Score",
      description: "Your brand visibility vs competitors",
      yAxisLabel: "Score",
      yAxisDomain: [0, 100],
      valueSuffix: "",
      showSeriesToggles: false,
      chartType: "area",
      showTrend: true,
};

export const VisibilityChart = ({ brandId, period, activity = null, chartType }: ChartProps) => {
      const trpc = useTRPC();

      const { data: chartResponse, isLoading } = useQuery(
            trpc.charts.getDashboardMainChartData.queryOptions({ brandId, ...getDisplayPeriodQuery(period) }),
      );

      const chartData = useMemo<ChartDataPoint[]>(() => {
            if (!chartResponse?.chartData) return [];
            return chartResponse.chartData.map((point) => {
                  const dataPoint: ChartDataPoint = {
                        date: point.date,
                        label: point.label,
                        intervalLabel: point.intervalLabel,
                  };
                  point.brandScores.forEach((brandScore) => {
                        dataPoint[brandScore.brandId] = brandScore.score;
                  });
                  return dataPoint;
            });
      }, [chartResponse]);

      const brands = useMemo<ChartBrand[]>(() => {
            return (chartResponse?.brands ?? []).map((brand) => ({
                  id: brand.id,
                  name: brand.name,
                  logo: brand.logo,
                  isMainBrand: brand.isMainBrand,
            }));
      }, [chartResponse?.brands]);

      const config = useMemo(
            () => ({
                  ...visibilityConfig,
                  chartType: chartType ?? visibilityConfig.chartType,
            }),
            [chartType],
      );

      return (
            <BaseChart
                  config={config}
                  chartData={chartData}
                  brands={brands}
                  isLoading={isLoading}
                  activity={activity}
            />
      );
};

// ============================================================================
// MENTION RATE CHART - Shows mention rate vs competitors
// ============================================================================

const mentionConfig: BaseChartConfig = {
      title: "Mention Rate",
      tooltip: <MentionRateTooltip />,
      separatedHeader: true,
      yAxisLabel: "Mention %",
      yAxisDomain: [0, 100],
      valueSuffix: "%",
      showSeriesToggles: false,
      chartType: "line",
      showTrend: true,
      dataSeries: [{ suffix: "", label: "", strokeWidth: 1.5, dotRadius: 3 }],
};

export const MentionChart = ({
      brandId,
      period,
      activity = null,
      chartType,
      compact = false,
      highlightedCompetitorId = null,
      showCompetitors = true,
      hiddenCompetitorIds,
}: ChartProps) => {
      const trpc = useTRPC();

      const { data: chartResponse, isLoading } = useQuery(
            trpc.charts.getMentionChartData.queryOptions({ brandId, ...getDisplayPeriodQuery(period) }),
      );

      const chartData = useMemo<ChartDataPoint[]>(() => {
            if (!chartResponse?.chartData) return [];
            return chartResponse.chartData.map((point) => {
                  const dataPoint: ChartDataPoint = {
                        date: point.date,
                        label: point.label,
                        intervalLabel: point.intervalLabel,
                  };
                  // Extract percentage for each brand
                  Object.entries(point.brandMentions).forEach(([brandId, data]) => {
                        dataPoint[brandId] = data.percentage;
                  });
                  return dataPoint;
            });
      }, [chartResponse]);

      const brands = useMemo<ChartBrand[]>(() => {
            return (chartResponse?.brands ?? []).map((brand) => ({
                  id: brand.id,
                  name: brand.name,
                  logo: brand.logo,
                  isMainBrand: brand.isMainBrand,
            }));
      }, [chartResponse?.brands]);

      const config = useMemo(
            () => ({
                  ...mentionConfig,
                  chartType: chartType ?? mentionConfig.chartType,
            }),
            [chartType],
      );

      return (
            <BaseChart
                  config={config}
                  chartData={chartData}
                  brands={brands}
                  isLoading={isLoading}
                  activity={activity}
                  showElapsedTime={false}
                  compact={compact}
                  highlightedBrandId={highlightedCompetitorId}
                  showCompetitors={showCompetitors}
                  hiddenBrandIds={hiddenCompetitorIds}
            />
      );
};

// ============================================================================
// SENTIMENT SCORE CHART - Shows sentiment vs competitors
// ============================================================================

const sentimentConfig: BaseChartConfig = {
      title: "Sentiment Score",
      tooltip: <SentimentScoreTooltip />,
      separatedHeader: true,
      yAxisLabel: "Sentiment",
      yAxisDomain: [0, 100],
      showSeriesToggles: false,
      chartType: "line",
      showTrend: true,
      dataSeries: [{ suffix: "", label: "", strokeWidth: 1.5, dotRadius: 3 }],
};

export const SentimentChart = ({
      brandId,
      period,
      activity = null,
      chartType,
      compact = false,
      highlightedCompetitorId = null,
      showCompetitors = true,
      hiddenCompetitorIds,
}: ChartProps) => {
      const trpc = useTRPC();

      const { data: chartResponse, isLoading } = useQuery(
            trpc.charts.getSentimentChartData.queryOptions({ brandId, ...getDisplayPeriodQuery(period) }),
      );

      const chartData = useMemo<ChartDataPoint[]>(() => {
            if (!chartResponse?.chartData) return [];
            return chartResponse.chartData.map((point) => {
                  const dataPoint: ChartDataPoint = {
                        date: point.date,
                        label: point.label,
                        intervalLabel: point.intervalLabel,
                  };
                  // Extract average sentiment for each brand, fill missing values with 0
                  Object.entries(point.brandSentiments).forEach(([brandId, data]) => {
                        dataPoint[brandId] = data.avg ?? 0;
                  });
                  return dataPoint;
            });
      }, [chartResponse]);

      const brands = useMemo<ChartBrand[]>(() => {
            return (chartResponse?.brands ?? []).map((brand) => ({
                  id: brand.id,
                  name: brand.name,
                  logo: brand.logo,
                  isMainBrand: brand.isMainBrand,
            }));
      }, [chartResponse?.brands]);

      const config = useMemo(
            () => ({
                  ...sentimentConfig,
                  chartType: chartType ?? sentimentConfig.chartType,
            }),
            [chartType],
      );

      return (
            <BaseChart
                  config={config}
                  chartData={chartData}
                  brands={brands}
                  isLoading={isLoading}
                  activity={activity}
                  showElapsedTime={false}
                  compact={compact}
                  highlightedBrandId={highlightedCompetitorId}
                  showCompetitors={showCompetitors}
                  hiddenBrandIds={hiddenCompetitorIds}
            />
      );
};

// ============================================================================
// RANKING CHART - Shows ranking position vs competitors
// ============================================================================

const rankingConfig: BaseChartConfig = {
      title: "Ranking",
      tooltip: <RankingTooltip />,
      separatedHeader: true,
      yAxisLabel: "Position",
      yAxisDomain: [1, 10],
      yAxisReversed: true, // Lower is better, so 1 should be at top
      treatNullAsNoData: true,
      showSeriesToggles: false,
      chartType: "line",
      showTrend: true,
      dataSeries: [{ suffix: "", label: "", strokeWidth: 1.5, dotRadius: 3 }],
      valueFormatter: (value) => {
            if (value === null) return "N/A";
            return `#${value.toFixed(1)}`;
      },
};

export const RankingChart = ({
      brandId,
      period,
      activity = null,
      chartType,
      compact = false,
      highlightedCompetitorId = null,
      showCompetitors = true,
      hiddenCompetitorIds,
}: ChartProps) => {
      const trpc = useTRPC();

      const { data: chartResponse, isLoading } = useQuery(
            trpc.charts.getRankingChartData.queryOptions({ brandId, ...getDisplayPeriodQuery(period) }),
      );

      const chartData = useMemo<ChartDataPoint[]>(() => {
            if (!chartResponse?.chartData) return [];
            return chartResponse.chartData.map((point) => {
                  const dataPoint: ChartDataPoint = {
                        date: point.date,
                        label: point.label,
                        intervalLabel: point.intervalLabel,
                  };
                  // Extract avg ranking for each brand
                  Object.entries(point.brandRankings).forEach(([brandId, data]) => {
                        dataPoint[brandId] = data.avg;
                  });
                  return dataPoint;
            });
      }, [chartResponse]);

      const brands = useMemo<ChartBrand[]>(() => {
            return (chartResponse?.brands ?? []).map((brand) => ({
                  id: brand.id,
                  name: brand.name,
                  logo: brand.logo,
                  isMainBrand: brand.isMainBrand,
            }));
      }, [chartResponse?.brands]);

      const config = useMemo(
            () => ({
                  ...rankingConfig,
                  chartType: chartType ?? rankingConfig.chartType,
            }),
            [chartType],
      );

      return (
            <BaseChart
                  config={config}
                  chartData={chartData}
                  brands={brands}
                  isLoading={isLoading}
                  activity={activity}
                  showElapsedTime={false}
                  compact={compact}
                  highlightedBrandId={highlightedCompetitorId}
                  showCompetitors={showCompetitors}
                  hiddenBrandIds={hiddenCompetitorIds}
            />
      );
};
